import { LngLatBounds} from 'mapbox-gl';
import GeoLocation from './GeoLocation';
import type { GeoFieldPoint } from './GeoLocation';

/**
 * Geofield de Drupal con los limites, tal cual llega del backend. Comparte
 * lat/lon con el punto porque el backend envia ambos en el mismo objeto.
 * @see GeoFieldPoint
 */
export interface GeoFieldBounds extends Partial<GeoFieldPoint> {
    left?: string | number;
    right?: string | number;
    top?: string | number;
    bottom?: string | number;
}

/**
 * Stores geographical bounds data, wrapping MapBox's LngLatBounds
 *
 * @see https://docs.mapbox.com/mapbox-gl-js/api/geography/#lnglatbounds
 */
class GeoBounds extends LngLatBounds {
    // Properties
    // (LngLatLike?) sw
    // (LngLatLike?) ne
    
    /**
     * Construct from Mapbox LngLat
     */
    static fromLngLatBounds(bounds: LngLatBounds): GeoBounds {
      return new GeoBounds(bounds.sw, bounds.ne);
    }
    
    /**
     * From raw object data.
     * with 'top', 'right', 'left', 'bottom'
     */
    static fromGeoData(data: GeoFieldBounds): GeoBounds | undefined {
      if (data.left && data.bottom && data.right && data.top) {
          // Los valores llegan como cadena; LngLat ya los convertia con +valor.
          const sw = new GeoLocation(Number(data.left), Number(data.bottom));
          const ne = new GeoLocation(Number(data.right), Number(data.top));
          return new GeoBounds(sw, ne);
      }
      return undefined;
    }
     
    /**
     * Check whether current bounds contain new ones.
     */
    containsBounds(bounds: LngLatBounds): boolean {
      return this.contains(bounds.getSouthWest())
        && this.contains(bounds.getNorthEast())
        && this.contains(bounds.getNorthWest())
        && this.contains(bounds.getSouthEast());
    }    
        
    /**
     * Convert to query string for REST request.
     */
    toQueryString(): string {
        const geoLatMin = this._sw.lat;
        const geoLatMax = this._ne.lat;
        const geoLonMin = this._sw.lng;
        const geoLonMax = this._ne.lng;
        
        let urlData = '';
        urlData += 'geo_lat[min]=' + geoLatMin;
        urlData += '&geo_lat[max]=' + geoLatMax;
        urlData += '&geo_lon[min]=' + geoLonMin;
        urlData += '&geo_lon[max]=' + geoLonMax;
        return urlData; 
    }
}

export default GeoBounds;
